import { isChatApiError } from '@alternativamais/chat-core';
import { useState } from 'react';

import { Icone } from '../../componentes/Icone';
import { mensagemDeErro } from '../../componentes/Avisos';
import { useSessao } from '../../chat/contexto';
import { useAgora } from '../../chat/ganchos';

/** Login errado: o que dizer (A15), com as tentativas que faltam. */
function textoDoErro(falha: unknown): string {
  if (isChatApiError(falha) && falha.status === 401) {
    const faltam = falha.remainingAttempts;
    const minutos = falha.lockMinutes ?? 15;
    if (faltam === null || faltam <= 0) return 'Usuário ou senha incorretos.';
    return faltam === 1
      ? `Usuário ou senha incorretos. Mais 1 tentativa errada bloqueia o acesso por ${minutos} minutos.`
      : `Usuário ou senha incorretos. Mais ${faltam} tentativas erradas bloqueiam o acesso por ${minutos} minutos.`;
  }
  return mensagemDeErro(falha);
}

/** Entrar (A1, A15, A10): e-mail ou usuário e senha da conta da Alternativa. */
export function Entrar() {
  const { entrar, aviso } = useSessao();
  const [login, setLogin] = useState('');
  const [senha, setSenha] = useState('');
  const [mostrar, setMostrar] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [bloqueadoAte, setBloqueadoAte] = useState<number | null>(null);
  const [esqueci, setEsqueci] = useState(false);

  const enviar = async (evento: React.FormEvent) => {
    evento.preventDefault();
    if (!login.trim() || !senha) {
      setErro('Preencha o usuário e a senha.');
      return;
    }
    setEnviando(true);
    setErro(null);
    try {
      await entrar(login.trim(), senha);
    } catch (falha) {
      if (isChatApiError(falha) && falha.status === 429) {
        setBloqueadoAte(Date.now() + (falha.retryAfter ?? 15 * 60) * 1000);
      } else {
        setErro(textoDoErro(falha));
      }
      setSenha('');
      setEnviando(false);
    }
  };

  if (bloqueadoAte) {
    return (
      <Bloqueado
        ate={bloqueadoAte}
        aoVoltar={() => {
          setBloqueadoAte(null);
          setErro(null);
        }}
      />
    );
  }

  return (
    <div className="flex h-full overflow-hidden bg-chat">
      <section
        aria-label="Alternativa Chat"
        className="relative hidden w-[620px] shrink-0 flex-col bg-rail px-[72px] pt-16 pb-12 lg:flex"
      >
        <div className="flex grow flex-col justify-center gap-7">
          <span className="flex size-[88px] items-center justify-center rounded-[22px] bg-destaque text-destaque-texto">
            <Icone nome="logo" tamanho={52} />
          </span>
          <div className="flex flex-col gap-3.5">
            <h1 className="m-0 font-titulo text-[44px] leading-[1.05] font-bold tracking-[-.01em]">
              Alternativa Chat
            </h1>
            <p className="m-0 max-w-[430px] text-lg leading-normal text-texto-2">
              Comunicação interna da Alternativa: setores, NOC e alertas num
              lugar só.
            </p>
          </div>
          <ul className="mt-2 flex list-none flex-col gap-3.5 p-0 text-[15px] text-texto-2">
            {[
              ['hash', 'Canais por setor e por unidade'],
              ['alerta', 'Alertas de OLTs e rádios assim que acontecem'],
              ['thread', 'Uma thread para cada chamado ou ocorrência'],
            ].map(([icone, texto]) => (
              <li key={texto} className="flex items-center gap-3">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-chat text-destaque">
                  <Icone nome={icone as 'hash'} tamanho={18} />
                </span>
                {texto}
              </li>
            ))}
          </ul>
        </div>
        <p className="m-0 flex items-center gap-2 text-[13px] text-apagado">
          <Icone nome="escudo" tamanho={16} />
          Acesso restrito a colaboradores da Alternativa.
        </p>
      </section>
      <main className="flex min-w-0 grow items-center justify-center p-4">
        <div className="flex w-[440px] max-w-full flex-col gap-[22px] rounded-xl bg-elevado p-8 shadow-[0_12px_32px_rgba(0,0,0,.45)]">
          <div className="flex flex-col gap-1.5">
            <h2 className="m-0 font-titulo text-[28px] leading-tight font-bold">
              Entrar
            </h2>
            <p className="m-0 text-[15px] text-texto-2">
              Use o e-mail corporativo ou seu usuário.
            </p>
          </div>
          {aviso && (
            <p
              role="status"
              className="m-0 flex items-start gap-2 rounded-lg bg-destaque-suave px-3 py-2.5 text-sm text-texto"
            >
              <Icone
                nome="info"
                tamanho={18}
                className="mt-px shrink-0 text-destaque"
              />
              {aviso}
            </p>
          )}
          <form
            aria-label="Entrar com usuário e senha"
            className="flex flex-col gap-4"
            onSubmit={enviar}
            noValidate
          >
            <div className="flex flex-col gap-2">
              <label
                htmlFor="entrar-email"
                className="text-sm font-semibold text-texto-2"
              >
                E-mail ou usuário
              </label>
              <input
                id="entrar-email"
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                autoFocus
                value={login}
                onChange={(evento) => setLogin(evento.target.value)}
                placeholder="nome.sobrenome"
                className="h-11 rounded-lg border border-borda bg-chat px-3 text-[15px] text-texto outline-none placeholder:text-texto-3 focus:border-destaque focus:shadow-[0_0_0_3px_rgba(245,154,69,.2)]"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label
                htmlFor="entrar-senha"
                className="text-sm font-semibold text-texto-2"
              >
                Senha
              </label>
              <div
                className={`flex h-11 items-center gap-1 rounded-lg border bg-chat pr-1 pl-3 focus-within:border-destaque focus-within:shadow-[0_0_0_3px_rgba(245,154,69,.2)] ${
                  erro ? 'border-ocupado' : 'border-borda'
                }`}
              >
                <input
                  id="entrar-senha"
                  type={mostrar ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={senha}
                  aria-invalid={!!erro}
                  aria-describedby={erro ? 'entrar-erro' : undefined}
                  onChange={(evento) => setSenha(evento.target.value)}
                  className="h-10 min-w-0 grow border-0 bg-transparent text-[15px] text-texto outline-none"
                />
                <button
                  type="button"
                  aria-label={mostrar ? 'Esconder senha' : 'Mostrar senha'}
                  aria-pressed={mostrar}
                  onClick={() => setMostrar(!mostrar)}
                  className="flex h-[34px] items-center gap-1.5 rounded-md px-2.5 text-[13px] font-semibold text-texto-2 hover:text-texto"
                >
                  <Icone
                    nome={mostrar ? 'olho-fechado' : 'olho'}
                    tamanho={18}
                  />
                  {mostrar ? 'Esconder' : 'Mostrar'}
                </button>
              </div>
              {erro && (
                <p
                  id="entrar-erro"
                  role="alert"
                  className="m-0 flex items-start gap-1.5 text-[13px] leading-[1.45] text-ocupado"
                >
                  <Icone
                    nome="alerta"
                    tamanho={16}
                    className="mt-px shrink-0"
                  />
                  <span>{erro}</span>
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={enviando}
              className="mt-1 flex h-11 items-center justify-center rounded-lg bg-destaque text-[15px] font-semibold text-destaque-texto hover:brightness-105 disabled:opacity-60"
            >
              {enviando ? 'Entrando…' : 'Entrar'}
            </button>
          </form>
          <div className="flex flex-col gap-2 border-t border-borda pt-[18px] text-sm leading-normal text-texto-3">
            <button
              type="button"
              aria-expanded={esqueci}
              onClick={() => setEsqueci(!esqueci)}
              className="self-start font-semibold text-link hover:underline"
            >
              Esqueci minha senha
            </button>
            {esqueci && (
              <p role="status" className="m-0 text-texto-2">
                Peça uma senha nova a quem administra o sistema na Alternativa.
                Ela vale só para entrar uma vez: em seguida você cria a sua.
              </p>
            )}
            <p className="m-0">
              Primeiro acesso? Abra o link que você recebeu de quem administra o
              sistema.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

/** Acesso bloqueado (A10): muitas senhas erradas, com a contagem regressiva. */
function Bloqueado({ ate, aoVoltar }: { ate: number; aoVoltar: () => void }) {
  const agora = useAgora(1000);
  const resta = Math.max(0, Math.ceil((ate - agora) / 1000));
  const minutos = String(Math.floor(resta / 60)).padStart(2, '0');
  const segundos = String(resta % 60).padStart(2, '0');
  return (
    <div className="flex h-full items-center justify-center bg-chat p-4">
      <div className="flex w-[440px] max-w-full flex-col items-center gap-4 rounded-xl bg-elevado p-8 text-center shadow-[0_12px_32px_rgba(0,0,0,.45)]">
        <span className="flex size-14 items-center justify-center rounded-full bg-ativo text-ocupado">
          <Icone nome="cadeado" tamanho={28} />
        </span>
        <h2 className="m-0 font-titulo text-2xl font-bold">
          Acesso bloqueado temporariamente
        </h2>
        <p className="m-0 text-[15px] leading-normal text-texto-2">
          Foram muitas tentativas de senha incorreta. Tente de novo em:
        </p>
        <p
          role="timer"
          aria-live="off"
          className="m-0 font-titulo text-[40px] font-bold tabular-nums"
        >
          {minutos}:{segundos}
        </p>
        <button
          type="button"
          disabled={resta > 0}
          onClick={aoVoltar}
          className="flex h-11 w-full items-center justify-center rounded-lg bg-destaque text-[15px] font-semibold text-destaque-texto hover:brightness-105 disabled:opacity-50"
        >
          Voltar ao login
        </button>
        <p className="m-0 text-[13px] leading-normal text-texto-3">
          Se não foi você, avise quem administra o sistema.
        </p>
      </div>
    </div>
  );
}
